import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, CheckCircle, AlertCircle, Loader } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getApiErrorMessage } from "../lib/apiError";

export default function Login() {
  const navigate = useNavigate();
  const { customerLogin } = useAuth();

  const [form, setForm] = useState({
    email: "",
    password: ""
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Validation
      if (!form.email.trim()) {
        setError("E-Mail ist erforderlich");
        setLoading(false);
        return;
      }
      if (!form.password.trim()) {
        setError("Passwort ist erforderlich");
        setLoading(false);
        return;
      }

      await customerLogin(form.email, form.password);
      
      const pendingRedirect = sessionStorage.getItem("redwork_pending_redirect");
      if (pendingRedirect) {
        sessionStorage.removeItem("redwork_pending_redirect");
        navigate(pendingRedirect, { replace: true });
        return;
      }

      const pendingProd = sessionStorage.getItem("redwork_pending_product_id");
      if (pendingProd) {
        navigate(`/dashboard/products/${pendingProd}`, { replace: true });
        return;
      }

      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err, "Anmeldung fehlgeschlagen"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090F] text-slate-100 flex items-center justify-center p-6 selection:bg-[#E63946] selection:text-white">
      <div className="w-full max-w-md rounded-[32px] bg-[#0F1422] border border-white/10 p-8 sm:p-10 shadow-2xl relative overflow-hidden">
        {/* Subtle accent glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-transparent via-[#E63946] to-transparent"></div>
        
        {/* Header */}
        <div className="mb-8 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#E63946] font-bold">Kundenportal</p>
          <h1 className="mt-2 text-2xl sm:text-3xl font-black text-white tracking-tight">Willkommen zurück</h1>
          <p className="mt-2 text-sm text-slate-400">Melden Sie sich an, um auf Ihr Schweizer Dashboard zuzugreifen.</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          
          {/* Email */}
          <label className="block text-xs font-semibold text-slate-300">
            E-Mail-Adresse
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="name@ihre-domain.ch"
              required
              disabled={loading}
              className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#FF7A00] focus:bg-white/10 outline-none disabled:opacity-50 transition"
            />
          </label>

          {/* Password */}
          <label className="block text-xs font-semibold text-slate-300">
            Passwort
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="••••••••••••"
              required
              disabled={loading}
              className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#FF7A00] focus:bg-white/10 outline-none disabled:opacity-50 transition"
            />
          </label>

          {/* Error Alert */}
          {error && (
            <div className="rounded-2xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-xs text-red-400 flex items-start gap-2.5">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#E63946] px-6 py-3.5 text-sm font-bold text-white hover:bg-[#d52b38] shadow-lg shadow-red-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {loading ? (
              <>
                <Loader size={18} className="animate-spin" />
                Wird angemeldet...
              </>
            ) : (
              <>
                Anmelden
                <ArrowRight size={18} />
              </>
            )}
          </button>

          {/* Links */}
          <div className="flex items-center justify-between pt-1">
            <Link
              to="/password-reset"
              className="text-xs font-medium text-slate-400 hover:text-white transition"
            >
              Passwort vergessen?
            </Link>
            <Link
              to="/register"
              className="text-xs font-bold text-[#FF7A00] hover:text-[#ff922d] transition"
            >
              Konto erstellen
            </Link>
          </div>
        </form>

        {/* Security Info Box */}
        <div className="mt-8 rounded-2xl bg-white/[0.03] border border-white/5 p-4 text-xs text-slate-400">
          <div className="flex items-start gap-2.5">
            <CheckCircle size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-slate-200">Sichere Verbindung</p>
              <p className="mt-1 text-slate-400 leading-relaxed">
                256-Bit SSL/TLS-Verschlüsselung auf Schweizer Hosting-Infrastruktur.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}